import { StackContext } from '@pubstack/common/src/stackContext'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useContextKeys } from '~/api/adm-api.hook'
import { useContexts, useUpdateContext } from '~/api/adm/contexts.api'
import { useToast } from '~/components/Toast/useToasts'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { PureAdmContextsPage } from './PureAdmContextsPage'

export const AdmContextsPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const breadcrumbs = useBreadcrumbs()
  // The live contexts only: the archived ones have a page of their own, so this list never has to group
  // them last and never offers a way to bring one back into it by accident.
  const { data: contexts, isFetching, isError } = useContexts({ withStacksUse: true, enabled: true })
  const { all: allContextKeys } = useContextKeys(null)
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const toast = useToast()
  const { mutateAsync: updateContext } = useUpdateContext()

  async function onCreateContext() {
    try {
      const contextKeys = await allContextKeys.get()
      if (contextKeys?.length > 0) {
        navigate(`/monetization/one/context/contexts/new`)
      } else {
        setIsModalVisible(true)
      }
    } catch (_e) {
      toast.alert('An error occurred while trying to fetch the context keys of your scope.')
    }
  }

  // Archiving only: the way back lives on the archived page, and the API refuses it here anyway.
  const archiveContext = async (context: StackContext) => {
    try {
      await updateContext({ ...context, enabled: false })
      toast.success(
        <>
          <strong>{context.name}</strong> was successfuly archived.
        </>
      )
    } catch (error) {
      // The API refuses a context a stack still points at, and says which one and why — worth more than
      // "an error occurred", which is all there is to say about the failures that have no reason to give.
      const reason = (error as { description?: { message?: string } })?.description?.message
      toast.alert(
        reason ?? (
          <>
            An error occurred while trying to archive <strong>{context.name}</strong>.
          </>
        )
      )
    }
  }

  return (
    <PureAdmContextsPage
      contexts={contexts}
      isLoading={isFetching}
      isError={isError}
      onCreateContext={onCreateContext}
      onArchiveContext={archiveContext}
      onNavigateToArchived={() => navigate('/monetization/one/context/contexts/archived')}
      breadcrumbs={breadcrumbs}
      isModalVisible={isModalVisible}
      setIsOpen={setIsModalVisible}
      onCreateContextKey={() => navigate(`/monetization/one/context/context-keys`)}
    />
  )
}
